'use client';

import {
  type PointerEvent,
  type ReactNode,
  useCallback,
  useRef,
  useState,
} from 'react';

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import { cn } from '@kit/ui/utils';

/**
 * One scrollable row of booking filter chips (design-system §6): a forward
 * arrow while the row overflows, a back arrow once scrolled, mirrored for RTL.
 * The selected chip (`aria-pressed` or `data-active`) is scrolled into view on mount.
 * Touch swipes scroll natively; a mouse can click and drag the row.
 *
 * `arrows="overlay"` (the sport strip) fades the overflowing edges instead and
 * floats small round arrows over them, only on hover-capable devices and only
 * toward the side that still has more.
 */
export function ScrollableChipRow({
  children,
  backLabel,
  forwardLabel,
  testId,
  contentClassName,
  arrows = 'inline',
}: {
  children: ReactNode;
  backLabel: string;
  forwardLabel: string;
  /** Prefix for the `-scroll-back`, `-scroll-forward` and `-scroll-track` test ids. */
  testId: string;
  contentClassName?: string;
  arrows?: 'inline' | 'overlay';
}) {
  const { i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  const scrollRef = useRef<HTMLDivElement>(null);
  const backButtonRef = useRef<HTMLButtonElement>(null);
  const dragRef = useRef<{
    startX: number;
    startScroll: number;
    moved: boolean;
  } | null>(null);
  const suppressClickRef = useRef(false);
  const [scrollState, setScrollState] = useState({
    hasOverflow: false,
    canScrollBack: false,
    canScrollForward: false,
  });

  // Observe the viewport and content so arrows follow resizing, fonts and touch scrolling.
  const observeScroll = useCallback((track: HTMLDivElement | null) => {
    if (!track) return;
    const row = track.parentElement;
    const content = track.firstElementChild;
    if (!row || !content) return;

    scrollRef.current = track;
    let revealSelection = true;

    const update = () => {
      if (track.clientWidth === 0) return;

      if (revealSelection) {
        revealSelection = false;
        const selected = track.querySelector(
          '[aria-pressed="true"], [data-active="true"]',
        );
        if (selected) {
          const selectedBounds = selected.getBoundingClientRect();
          const trackBounds = track.getBoundingClientRect();
          if (
            selectedBounds.left < trackBounds.left ||
            selectedBounds.right > trackBounds.right
          ) {
            track.scrollBy({
              left:
                selectedBounds.left +
                selectedBounds.width / 2 -
                trackBounds.left -
                trackBounds.width / 2,
              behavior: 'instant',
            });
          }
        }
      }

      const offset = Math.abs(track.scrollLeft);
      const next = {
        hasOverflow: content.scrollWidth > row.clientWidth + 1,
        canScrollBack: offset > 1,
        canScrollForward: offset < track.scrollWidth - track.clientWidth - 1,
      };
      if (
        !next.canScrollBack &&
        document.activeElement === backButtonRef.current
      ) {
        content.querySelector<HTMLButtonElement>('button')?.focus({
          preventScroll: true,
        });
      }
      setScrollState((current) =>
        current.hasOverflow === next.hasOverflow &&
        current.canScrollBack === next.canScrollBack &&
        current.canScrollForward === next.canScrollForward
          ? current
          : next,
      );
    };

    const observer = new ResizeObserver(update);
    [row, track, content].forEach((element) => observer.observe(element));
    track.addEventListener('scroll', update, { passive: true });

    return () => {
      observer.disconnect();
      track.removeEventListener('scroll', update);
      scrollRef.current = null;
    };
  }, []);

  const scroll = (forward: boolean) => {
    const track = scrollRef.current;
    if (!track) return;
    track.scrollBy({
      left: track.clientWidth * 0.8 * (forward !== isRTL ? 1 : -1),
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'instant'
        : 'smooth',
    });
  };

  const startDrag = (event: PointerEvent<HTMLDivElement>) => {
    suppressClickRef.current = false;
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    dragRef.current = {
      startX: event.clientX,
      startScroll: event.currentTarget.scrollLeft,
      moved: false,
    };
  };

  const moveDrag = (event: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    const distance = event.clientX - drag.startX;
    if (!drag.moved) {
      if (Math.abs(distance) < 5) return;
      drag.moved = true;
      event.currentTarget.setPointerCapture(event.pointerId);
    }
    event.currentTarget.scrollLeft = drag.startScroll - distance;
  };

  // A drag must not also select the chip it started on.
  const endDrag = () => {
    suppressClickRef.current = dragRef.current?.moved ?? false;
    dragRef.current = null;
  };

  const overlay = arrows === 'overlay';
  const fadeLeft = isRTL
    ? scrollState.canScrollForward
    : scrollState.canScrollBack;
  const fadeRight = isRTL
    ? scrollState.canScrollBack
    : scrollState.canScrollForward;
  const edge = (fade: boolean) => (fade ? 'transparent' : '#000');
  const edgeMask = overlay
    ? `linear-gradient(to right, ${edge(fadeLeft)}, #000 2.5rem, #000 calc(100% - 2.5rem), ${edge(fadeRight)})`
    : undefined;
  // `!min-h-0`: the native theme gives every button a 44px minimum height.
  const overlayArrowClass =
    'absolute top-1/2 z-10 size-8 !min-h-0 -translate-y-1/2 rounded-full [@media(hover:none)]:hidden';

  return (
    <div
      className={
        overlay ? 'relative min-w-0' : 'flex min-w-0 items-center gap-1'
      }
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {scrollState.hasOverflow && scrollState.canScrollBack && (
        <Button
          ref={backButtonRef}
          type="button"
          variant={overlay ? 'outline' : 'ghost'}
          size="icon"
          className={
            overlay ? cn(overlayArrowClass, 'start-1') : 'size-11 shrink-0'
          }
          aria-label={backLabel}
          data-test={`${testId}-scroll-back`}
          onClick={() => scroll(false)}
        >
          {isRTL ? (
            <ChevronRight className="size-4" />
          ) : (
            <ChevronLeft className="size-4" />
          )}
        </Button>
      )}
      <div
        ref={observeScroll}
        className="min-w-0 flex-1 overflow-x-auto overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        data-test={`${testId}-scroll-track`}
        style={
          edgeMask
            ? { maskImage: edgeMask, WebkitMaskImage: edgeMask }
            : undefined
        }
        onPointerDown={startDrag}
        onPointerMove={moveDrag}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onClickCapture={(event) => {
          if (!suppressClickRef.current) return;
          suppressClickRef.current = false;
          event.preventDefault();
          event.stopPropagation();
        }}
      >
        <div className={cn('flex w-max gap-1.5 p-1', contentClassName)}>
          {children}
        </div>
      </div>
      {scrollState.hasOverflow &&
        (!overlay || scrollState.canScrollForward) && (
          <Button
            type="button"
            variant={overlay ? 'outline' : 'ghost'}
            size="icon"
            className={
              overlay ? cn(overlayArrowClass, 'end-1') : 'size-11 shrink-0'
            }
            disabled={!scrollState.canScrollForward}
            aria-label={forwardLabel}
            data-test={`${testId}-scroll-forward`}
            onClick={() => scroll(true)}
          >
            {isRTL ? (
              <ChevronLeft className="size-4" />
            ) : (
              <ChevronRight className="size-4" />
            )}
          </Button>
        )}
    </div>
  );
}
